import React from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FiBook, FiCheckCircle, FiXCircle, FiCalendar, FiAward, FiBookOpen } from "react-icons/fi";
import PageTitle from "../../components/PageTitle";
import Loader from "../../components/Loader";
import ErrorResponse from "../../components/ErrorResponse";
import { axiosdelete, axiosget, axiospost } from "../../helpers/httprequest";
import "./Registration.css";
import Profile from "./Profile";
import swal from "../../helpers/swal";

const Registration = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["registerCourses"],
    queryFn: async () => {
      const req = await axiosget(`ms_course_registration`);
      return req?.data === undefined ? req : req?.data;
    },
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
  });

  console.log(data)

  return (
    <div className="registration-container">
      <PageTitle
        title="Semester Course Registration"
        sub="Select your courses for the semester. Please consult your department for guidance on required courses."
        icon={<FiBook />}
      />

      {/* Loading and Error States */}
      {isLoading && <Loader />}
      {error && <ErrorResponse type="danger" message={`Error: ${error.message}`} />}
      {data?.type !== undefined && <ErrorResponse type="danger" message={data?.message} />}
      <div className="display-grid">
        {/* Left Column - Profile and Quick Stats */}
        <div className="left-column">
          <Profile />
        </div>

        {/* Right Column - Main Content */}
        <div className="right-column">
          <UnitsCard totalUnits={data?.type !== undefined ? 0 : data?.filter((r) => r.isRegistered)?.reduce((total, course) => total + course.credit, 0)} />
        </div>
      </div>
      {/* Courses Grid */}
      {data?.type === undefined && (
        <div className="courses-grid">
          {data?.length === 0 ? (
            <div className="empty-state">
              <FiBook className="empty-icon" />
              <h3>No Courses Available</h3>
              <p>There are currently no courses available for registration</p>
            </div>
          ) : (
            data?.map((course, index) => <CourseCard key={`course-${index}`} course={course} />)
          )}
        </div>
      )}
    </div>
  );
};

const CourseCard = ({ course }) => {
  const [loading, setLoading] = React.useState(false);
  const queryClient = useQueryClient();

  const register = async () => {
    try {
      setLoading(true);
      const req = course?.isRegistered
        ? await axiosdelete(`ms_course_registration/${course._id}`)
        : await axiospost(`ms_course_registration`, { courseId: course._id });
      if (req?.status !== 200) throw new Error("Sorry, something went wrong try again");
      swal(req.data.type, req?.data?.message, "success");
      queryClient.invalidateQueries("registerCourses");
    } catch ({ message }) {
      swal("error", message, "error");
    }
    setLoading(false);
  };

  return (
    <div className={`course-card ${course?.isRegistered ? "registered" : "unregistered"}`}>
      <div className="card-header">
        <h3 className="course-code">{course?.code}1</h3>
        <span className="course-status">
          {course?.isRegistered ? (
            <>
              <FiCheckCircle className="status-icon" />
              <span>Registered</span>
            </>
          ) : (
            <>
              <FiXCircle className="status-icon" />
              <span>Not Registered</span>
            </>
          )}
        </span>
      </div>

      <div className="card-body">
        <h2 className="course-title">{course?.name}</h2>

        <div className="course-meta">
          <div className="meta-item">
            <FiAward className="meta-icon" />
            <span>{course?.credit} Credit Units</span>
          </div>
          <div className="meta-item">
            <FiCalendar className="meta-icon" />
            <span>
              Level {course?.level} | Semester {course?.semester}
            </span>
          </div>
        </div>
      </div>

      <div className="card-footer">
        <button disabled={loading} onClick={register} className={`action-button ${!course?.isRegistered ? "registered" : "unregistered"}`}>
          {loading ? "Please wait..." : course?.isRegistered ? "Drop Course" : "Register Now"}
        </button>
      </div>
    </div>
  );
};

const UnitsCard = ({ totalUnits, maxUnits = 24 }) => {
  const progressPercentage = Math.min((totalUnits / maxUnits) * 100, 100);

  return (
    <div className="units-card">
      <div className="card-header">
        <FiBookOpen className="card-icon" />
        <h3 className="card-title">Registered Units</h3>
      </div>

      <div className="card-content">
        <div className="units-display">
          <span className="units-count">{totalUnits}</span>
          <span className="units-label">out of {maxUnits} units</span>
        </div>

        <div className="progress-container1">
          <div className="progress-bar" style={{ width: `${progressPercentage}%` }}></div>
        </div>

        <div className="completion-status">
          <FiCheckCircle className="status-icon" />
          <span>{progressPercentage >= 100 ? "Maximum reached" : `${maxUnits - totalUnits} units remaining to maximum allowed`}</span>
        </div>
      </div>
    </div>
  );
};

export default Registration;
